import type { CodeBlock } from '@crowdex/ui/ai/code-block';
import type { ComponentProps } from 'react';

export function basename(path: string): string {
  return pathSegments(path).at(-1) ?? path;
}

/** A path's segments, split on `/` and on Windows' `\`. */
export function pathSegments(path: string): string[] {
  return path.split(/[\\/]/).filter(Boolean);
}

/**
 * The local path a `file:` URL names (decoded, `C:\…` on Windows, `\\host\…` for a share);
 * anything else is returned as is.
 */
export function fileUrlPath(uri: string): string {
  if (!uri.startsWith('file:')) return uri;
  let url: URL;
  try {
    url = new URL(uri);
  } catch {
    return uri;
  }
  let path: string;
  try {
    path = decodeURIComponent(url.pathname);
  } catch {
    path = url.pathname;
  }
  if (/^\/[A-Za-z]:/.test(path)) return path.slice(1).replaceAll('/', '\\');
  if (url.host.length > 0) return `\\\\${url.host}${path}`.replaceAll('/', '\\');
  return path;
}

/** A folder of the cloud's (its sandbox, `/workspace`, and the repositories it checks out). */
export function isCloudFolder(path: string): boolean {
  return path === '/workspace' || path.startsWith('/workspace/');
}

export function nextConversationWorkspace(
  currentWorkspace: string | null,
  selectedWorkspace: string | undefined,
  webOnly: boolean,
): string | null {
  if (selectedWorkspace !== undefined) return selectedWorkspace;
  return webOnly ? '/workspace' : currentWorkspace;
}

export function codeLanguage(path: string): ComponentProps<typeof CodeBlock>['language'] {
  const name = basename(path).toLowerCase();
  const extension = name.split('.').pop() ?? '';
  if (name === 'dockerfile') return 'dockerfile';
  if (name === 'makefile') return 'makefile';
  const languages: Record<string, ComponentProps<typeof CodeBlock>['language']> = {
    css: 'css',
    env: 'dotenv',
    go: 'go',
    html: 'html',
    java: 'java',
    js: 'javascript',
    json: 'json',
    jsx: 'jsx',
    md: 'markdown',
    php: 'php',
    py: 'python',
    rb: 'ruby',
    rs: 'rust',
    sh: 'shellscript',
    sql: 'sql',
    toml: 'toml',
    ts: 'typescript',
    tsx: 'tsx',
    xml: 'xml',
    yaml: 'yaml',
    yml: 'yaml',
  };
  return languages[extension] ?? 'markdown';
}
